import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import SiportFormFields from "@/components/SiportFormFields";
import Turnstile from "@/components/Turnstile";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Loader2, Lock, Send, FilePlus2, AlertTriangle } from "lucide-react";
import {
  EMPTY_FORM, todayISO, toPayload, validateForm, fmtTanggalPanjang, wibDateISO, msSampaiTengahMalamWIB,
} from "@/lib/siport";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

// Form publik (tanpa login) — diisi perawat/petugas poli dari HP.
export default function LaporSiport() {
  const [meta, setMeta] = useState(null); // {form_aktif, options, poliklinik, hari_ini}
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState({ ...EMPTY_FORM, tanggal_kejadian: todayISO() });
  const [website, setWebsite] = useState(""); // honeypot, disembunyikan dari manusia
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);
  const [nomor, setNomor] = useState("");
  const [tanggalInput, setTanggalInput] = useState(""); // diisi server (WIB)
  const [tsToken, setTsToken] = useState(""); // token Cloudflare Turnstile
  const [tsError, setTsError] = useState(false);
  const turnstileRef = useRef(null);
  const selisihJam = useRef(0); // jam server - jam perangkat (ms), supaya tidak bergantung jam HP yang salah

  const load = () => {
    setLoadError("");
    api.get("/public/siport/options")
      .then((r) => {
        if (r.data.waktu_server) selisihJam.current = Date.parse(r.data.waktu_server) - Date.now();
        setMeta(r.data);
        if (r.data.hari_ini) setForm((f) => ({ ...f, tanggal_kejadian: f.tanggal_kejadian || r.data.hari_ini }));
      })
      .catch(() => setLoadError("Gagal memuat form. Periksa koneksi internet lalu coba lagi."));
  };
  useEffect(() => {
    document.title = "Lapor SIPORT — SIMUTU";
    load();
  }, []);

  // Info "Tanggal Input Form" ikut berganti saat lewat tengah malam WIB walau halaman dibiarkan terbuka.
  // Timer tepat di tengah malam + cek berkala + saat tab/HP aktif lagi (timer bisa tertunda saat layar mati).
  const siapForm = !!meta?.form_aktif;
  useEffect(() => {
    if (!siapForm) return undefined;
    let timer;
    const perbarui = () => {
      const h = wibDateISO(Date.now() + selisihJam.current);
      setMeta((m) => (m && m.hari_ini !== h ? { ...m, hari_ini: h } : m));
      setForm((f) => (f.tanggal_kejadian !== h ? { ...f, tanggal_kejadian: h } : f));
    };
    const jadwalkan = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { perbarui(); jadwalkan(); }, msSampaiTengahMalamWIB(Date.now() + selisihJam.current) + 1000);
    };
    const saatAktif = () => { if (document.visibilityState === "visible") { perbarui(); jadwalkan(); } };
    perbarui();
    jadwalkan();
    const cek = setInterval(perbarui, 60000);
    document.addEventListener("visibilitychange", saatAktif);
    window.addEventListener("focus", saatAktif);
    return () => {
      clearTimeout(timer);
      clearInterval(cek);
      document.removeEventListener("visibilitychange", saatAktif);
      window.removeEventListener("focus", saatAktif);
    };
  }, [siapForm]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const errs = validateForm(form, meta.options, meta.hari_ini);
    setErrors(errs);
    const first = Object.keys(errs)[0];
    if (first) {
      document.getElementById(`field-${first}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (!tsToken) return;
    setSaving(true);
    try {
      const res = await api.post("/public/siport/reports",
        { ...toPayload(form, meta.options), website, turnstile_token: tsToken });
      setNomor(res.data.nomor_laporan);
      setTanggalInput(res.data.tanggal_input || "");
      window.scrollTo({ top: 0 });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail === "Form sedang ditutup") setMeta((m) => ({ ...m, form_aktif: false }));
      setSubmitError(apiError(detail) || "Gagal mengirim laporan");
    } finally {
      setSaving(false);
      turnstileRef.current?.reset(); // token Turnstile hanya sekali pakai
    }
  };

  const reset = () => {
    setForm({ ...EMPTY_FORM, tanggal_kejadian: meta?.hari_ini || todayISO(), poliklinik_id: form.poliklinik_id,
      poliklinik_manual: form.poliklinik_manual, poliklinik_nama_manual: form.poliklinik_nama_manual,
      nama_dokter_pengisi: form.nama_dokter_pengisi });
    setErrors({});
    setNomor("");
    window.scrollTo({ top: 0 });
  };

  let body;
  if (loadError) {
    body = (
      <Card className="border-slate-200"><CardContent className="p-6 text-center space-y-4">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
        <p className="text-slate-700">{loadError}</p>
        <Button onClick={load} className="h-12 w-full text-base">Coba lagi</Button>
      </CardContent></Card>
    );
  } else if (!meta) {
    body = <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  } else if (!meta.form_aktif) {
    body = (
      <Card className="border-slate-200" data-testid="siport-form-closed"><CardContent className="p-8 text-center space-y-3">
        <Lock className="w-12 h-12 text-slate-400 mx-auto" />
        <h2 className="text-xl font-bold text-slate-900">Form sedang ditutup</h2>
        <p className="text-sm text-slate-500">Silakan hubungi PJ Mutu IPRJT untuk informasi lebih lanjut.</p>
      </CardContent></Card>
    );
  } else if (nomor) {
    body = (
      <Card className="border-emerald-200" data-testid="siport-success"><CardContent className="p-8 text-center space-y-4">
        <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
        <h2 className="text-xl font-bold text-slate-900">Laporan berhasil dikirim</h2>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nomor Laporan</div>
          <div className="text-3xl font-extrabold text-brand-teal tabular-nums mt-1" data-testid="siport-nomor">{nomor}</div>
        </div>
        {tanggalInput && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tanggal Input Form</div>
            <div className="text-lg font-bold text-slate-900 mt-1" data-testid="siport-tanggal-input">{fmtTanggalPanjang(tanggalInput)}</div>
          </div>
        )}
        <p className="text-sm text-slate-500">Catat nomor ini bila diperlukan untuk konfirmasi.</p>
        <Button onClick={reset} className="h-12 w-full text-base" data-testid="siport-new-report">
          <FilePlus2 className="w-5 h-5 mr-2" /> Isi laporan baru
        </Button>
      </CardContent></Card>
    );
  } else {
    body = (
      <form onSubmit={submit} noValidate>
        <Card className="border-slate-200 shadow-sm overflow-hidden"><CardContent className="px-3 py-4 sm:p-6">
          <SiportFormFields form={form} setForm={setForm} options={meta.options} poliklinik={meta.poliklinik}
            poliManual={meta.poliklinik_manual} errors={errors} big hariIni={meta.hari_ini} tanggalOtomatis />
          {/* Honeypot anti-bot: tidak terlihat & tidak bisa difokus pengguna */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
            <label>Website<input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} name="website" /></label>
          </div>
          {Object.keys(errors).length > 0 && (
            <div className="mt-5 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
              Masih ada isian yang belum lengkap / belum benar. Periksa field bertanda merah.
            </div>
          )}
          {submitError && (
            <div className="mt-5 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="siport-submit-error">
              {submitError}
            </div>
          )}
          <div className="mt-6">
            <Turnstile ref={turnstileRef} siteKey={TURNSTILE_SITE_KEY}
              onToken={(t) => { setTsToken(t); if (t) setTsError(false); }} onError={() => setTsError(true)} />
            {tsError && (
              <div className="mt-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="siport-turnstile-error">
                Verifikasi keamanan gagal dimuat, muat ulang halaman.
              </div>
            )}
          </div>
          <Button type="submit" disabled={saving || !tsToken} className="w-full h-14 text-base font-bold mt-4" data-testid="siport-submit">
            {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}
            Kirim Laporan
          </Button>
          {!tsToken && !tsError && (
            <p className="mt-2 text-center text-xs text-slate-500">Tombol Kirim aktif setelah verifikasi keamanan selesai.</p>
          )}
        </CardContent></Card>
      </form>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="lapor-siport-page">
      <header className="bg-brand-teal text-white">
        <div className="max-w-xl mx-auto px-4 py-4 flex items-center gap-3">
          <img src="/logo-simutu.png" alt="SIMUTU" className="w-10 h-10 rounded-lg object-contain bg-white/10" />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-lg">Laporan SIPORT</div>
            <div className="text-xs text-white/90">Percepatan Layanan & Code Blue — Rawat Jalan IPRJT</div>
          </div>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-4 py-5 pb-16">
        {!nomor && meta?.form_aktif && (
          <p className="text-sm text-slate-500 mb-4">Isian bertanda <span className="text-rose-600">*</span> wajib diisi. Data pasien bersifat rahasia.</p>
        )}
        {body}
      </main>
    </div>
  );
}
